import React, { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import useWindowSize from "../../hooks/useWindowSize"
import { getPublicReviewCount } from "../../utils/publicReviewCount"

import AnimatedSection from "./AnimatedSection"
import HealMeVerifiedBadge from "./HealMeVerifiedBadge"
import Review from "./Review"
import ReviewStars from "./ReviewStars"
import SectionTitle from "./SectionTitle"
import SeeMore from "./SeeMore"

const ReviewsSection = ({ practice, theme, className, backgroundColor, color, showTitle = true, id = "reviews" }) => {
  backgroundColor ||= theme.colorBgLight
  color ||= theme.colorTextDark

  const { isMobile } = useWindowSize()

  const defaultReviewsShown = isMobile ? 4 : 10
  // Filter reviews to show only text reviews without video reviews
  const reviews = practice.reviews.filter((review) => review.review && !(review.hasCompletedVideoReview === true))
  const [expanded, setExpanded] = useState(false)
  const [expandedReviewSwitch, setExpandedReviewSwitch] = useState(false)

  const reviewsContainerRef = useRef(null)

  useEffect(() => {
    if (reviewsContainerRef.current) {
      reviewsContainerRef.current.style.maxHeight = expanded ? `${reviewsContainerRef.current.scrollHeight}px` : "0px"
    }
  }, [expanded, reviews, expandedReviewSwitch])

  // Trick to update the maxHeight of the reviews container when a review is expanded/collapsed
  const onExpand = () => {
    setExpandedReviewSwitch((prev) => !prev)
  }

  return (
    <section id={id} className={twMerge("scroll-mt-24 pt-24 md:px-5", className)} style={{ backgroundColor, color }}>
      <AnimatedSection className="container mx-auto">
        {showTitle && (
          <SectionTitle className="mb-4 text-center" theme={theme}>
            My Reviews
          </SectionTitle>
        )}
        <div className="mb-10 flex items-center justify-center gap-4">
          <ReviewStars rating={practice.averageRating} starColor={theme.colorReviewStars} />
          <span>{getPublicReviewCount(practice)} reviews</span>
        </div>
        <div className="mb-10 flex w-full justify-center">
          <HealMeVerifiedBadge theme={theme} backgroundColor={backgroundColor} color={color} />
        </div>
        <div className="grid grid-cols-2 gap-10 lg:grid-cols-1">
          {reviews.slice(0, defaultReviewsShown).map((review) => (
            <Review
              key={`review-${review.id}`}
              review={review}
              practice={practice}
              starColor={theme.colorReviewStars}
            />
          ))}
        </div>
        <div
          ref={reviewsContainerRef}
          className="mt-10 grid grid-cols-2 gap-10 overflow-hidden transition-all duration-1000 lg:grid-cols-1"
          style={{ maxHeight: 0 }}>
          {reviews.slice(defaultReviewsShown).map((review) => (
            <Review
              key={`review-${review.id}`}
              review={review}
              practice={practice}
              starColor={theme.colorReviewStars}
              onExpand={onExpand}
              onCollapse={onExpand}
            />
          ))}
        </div>
        {reviews.length > defaultReviewsShown && (
          <SeeMore
            className="pt-10 text-opacity-50"
            buttonClassName="hover:text-[var(--hover-color)]"
            buttonStyle={{ color, "--hover-color": theme.colorAccent }}
            buttonText="See more reviews"
            onClick={() => {
              if (expanded) document.getElementById("reviews").scrollIntoView({ behavior: "smooth" })
              setExpanded(!expanded)
            }}
          />
        )}
      </AnimatedSection>
    </section>
  )
}

export default ReviewsSection
